/**
 * Country → the currency OptiSigns bills that country in.
 *
 * OptiClaw states the rule but doesn't implement it — in `quote-gen` the LLM reading the
 * ticket is told "explicit request wins; else infer from country (UK→gbp, EU→eur,
 * Canada→cad, Australia→aud, US/other→usd)". This is that rule as code, so a staff quote
 * and an OptiClaw quote for the same customer land on the same currency.
 *
 * We sell in FIVE currencies (`CURRENCIES`), which is far fewer than the world has. So this
 * is not "what money does this country use" — Poland uses PLN and Japan uses JPY, and
 * neither is on the price list. It answers the narrower question: given a country, which of
 * our five do we invoice in. Everything unmapped is USD, which is also the catalog's
 * fallback currency, so an unmapped country prices rather than failing.
 *
 * WHY A GUESS IS SAFE HERE: the currency only ever pre-selects the dropdown. The rep sees
 * it and can change it, and the engine prices from `productPlans[currency]` either way. A
 * wrong guess is a visible wrong label on a form, not a silently wrong price — which is why
 * this infers freely while `matchProduct` refuses to fuzzy-match at all.
 */
import { isKnownCountry, normalizeCountry } from './countries';
import type { Currency } from './pricing/types';

/**
 * Sterling. GB plus the three Crown dependencies, which use the pound directly (Jersey,
 * Guernsey and Man issue local notes at parity, not a separate currency). Gibraltar is
 * deliberately absent — GIP is pegged but distinct, and it isn't a market we price.
 */
const GBP = ['GB', 'JE', 'GG', 'IM'];

/**
 * Euro. The 20 eurozone members, plus the microstates that use the euro by agreement
 * (AD, MC, SM, VA) and the two that adopted it unilaterally (ME, XK).
 *
 * NOT the EU: Poland, Sweden, Czechia, Hungary, Romania, Bulgaria and Denmark are EU
 * members with their own currencies, and none of those currencies is on our price list.
 * They fall through to USD below. If sales starts invoicing them in EUR, add them here —
 * the point of this list is that it's a billing decision, not a geography lookup.
 */
const EUR = [
  // Eurozone
  'AT', 'BE', 'CY', 'DE', 'EE', 'ES', 'FI', 'FR', 'GR', 'HR',
  'IE', 'IT', 'LT', 'LU', 'LV', 'MT', 'NL', 'PT', 'SI', 'SK',
  // Euro by monetary agreement, or adopted unilaterally
  'AD', 'MC', 'SM', 'VA', 'ME', 'XK',
];

const CAD = ['CA'];

/**
 * Australian dollar. AU only — New Zealand is deliberately NOT here. NZD is its own
 * currency and quoting an NZ customer in AUD would be a real pricing error, not a
 * cosmetic one; USD is the honest fallback until NZD is on the price list.
 */
const AUD = ['AU'];

const BY_CODE: Record<string, Currency> = {};
for (const [cur, codes] of [
  ['gbp', GBP],
  ['eur', EUR],
  ['cad', CAD],
  ['aud', AUD],
] as [Currency, string[]][]) {
  for (const code of codes) BY_CODE[code] = cur;
}

/**
 * The currency to bill `country` in, or `null` when there is nothing to go on — a blank
 * country, or free text that didn't resolve to a country at all.
 *
 * `null` is distinct from `'usd'` on purpose: it lets the caller leave the dropdown alone
 * instead of yanking it to USD because someone cleared the country field. A country that
 * DOES resolve but isn't in any list above returns `'usd'`, which is a real answer.
 */
export function currencyForCountry(country: string | undefined | null): Currency | null {
  const code = normalizeCountry(country ?? '');
  // normalizeCountry hands BACK the raw text when it can't place it, so `code` being
  // truthy is not enough — "Deutschland" comes through unresolved and must not be read as
  // "not on the euro list, therefore USD". Returning null leaves a currency the rep already
  // chose alone rather than yanking it to USD over a spelling we don't know.
  if (!isKnownCountry(code)) return null;
  return BY_CODE[code as string] ?? 'usd';
}
